/**
 * 权限缓存管理服务
 * @description 提供高性能的权限检查结果缓存机制
 * @author Claude Code Assistant
 * @date 2025-08-10
 * @version 2.0.0
 */

/**
 * 缓存条目接口
 */
interface CacheEntry {
  /** 缓存的权限检查结果 */
  value: boolean
  /** 过期时间戳 */
  expires: number
  /** 创建时间戳 */
  created: number
  /** 命中次数 */
  hits: number
}

/**
 * 缓存性能统计
 */
interface CacheStats {
  /** 总检查次数 */
  totalChecks: number
  /** 缓存命中次数 */
  hits: number
  /** 缓存未命中次数 */
  misses: number
  /** 缓存命中率 */
  hitRate: number
  /** 平均检查时间(ms) */
  averageCheckTime: number
  /** 当前缓存条目数 */
  cacheSize: number
  /** 最后清理时间 */
  lastCleanup: number
}

/**
 * 缓存配置
 */
interface CacheConfig {
  /** 缓存生存时间(毫秒)，默认5分钟 */
  ttl: number
  /** 最大缓存条目数，默认1000 */
  maxSize: number
  /** 自动清理间隔(毫秒)，默认1分钟 */
  cleanupInterval: number
  /** 是否启用缓存，默认true */
  enabled: boolean
}

/**
 * 权限缓存管理器
 */
export class PermissionCache {
  private cache = new Map<string, CacheEntry>()
  private stats: CacheStats = {
    totalChecks: 0,
    hits: 0,
    misses: 0,
    hitRate: 0,
    averageCheckTime: 0,
    cacheSize: 0,
    lastCleanup: Date.now(),
  }

  private config: CacheConfig
  private cleanupTimer?: NodeJS.Timeout
  private checkTimes: number[] = []

  constructor(config?: Partial<CacheConfig>) {
    this.config = {
      ttl: 5 * 60 * 1000, // 5分钟缓存
      maxSize: 1000, // 最大1000个条目
      cleanupInterval: 60 * 1000, // 1分钟清理一次
      enabled: true, // 默认启用
      ...config,
    }

    // 启动定期清理
    if (this.config.enabled) {
      this.startAutoCleanup()
    }

    // 开发环境调试信息
    // if (import.meta.env.DEV) {
    //   console.log('🚀 [权限缓存] 缓存服务已启动', this.config)
    // }
  }

  /**
   * 获取缓存的权限检查结果
   */
  get(key: string): boolean | null {
    if (!this.config.enabled)
      return null

    const startTime = performance.now()
    this.stats.totalChecks++

    const entry = this.cache.get(key)
    if (!entry) {
      this.recordMiss(performance.now() - startTime)
      return null
    }

    // 检查是否过期
    if (Date.now() > entry.expires) {
      this.cache.delete(key)
      this.recordMiss(performance.now() - startTime)
      return null
    }

    // 命中，更新统计信息
    entry.hits++
    this.recordHit(performance.now() - startTime)

    // if (import.meta.env.DEV && Math.random() < 0.1) { // 10%概率输出调试日志
    //   console.log(`✅ [权限缓存] 命中: ${key} (命中${entry.hits}次)`)
    // }

    return entry.value
  }

  /**
   * 设置权限检查结果到缓存
   */
  set(key: string, value: boolean): void {
    if (!this.config.enabled)
      return

    const now = Date.now()
    const entry: CacheEntry = {
      value,
      expires: now + this.config.ttl,
      created: now,
      hits: 0,
    }

    this.cache.set(key, entry)
    this.stats.cacheSize = this.cache.size

    // 如果超过最大大小，触发清理
    if (this.cache.size > this.config.maxSize) {
      this.cleanup()
    }

    // if (import.meta.env.DEV && Math.random() < 0.05) { // 5%概率输出调试日志
    //   console.log(`💾 [权限缓存] 设置: ${key} = ${value}`)
    // }
  }

  /**
   * 清理用户相关缓存
   * 当用户权限发生变更时调用
   */
  clearUserCache(userId: string): void {
    // let clearedCount = 0

    for (const [key] of this.cache) {
      if (key.includes(userId)) {
        this.cache.delete(key)
        // clearedCount++
      }
    }

    this.stats.cacheSize = this.cache.size

    // if (import.meta.env.DEV) {
    //   console.log(`🧹 [权限缓存] 清理用户缓存: ${userId}, 清理${clearedCount}条记录`)
    // }
  }

  /**
   * 清理角色相关缓存
   * 当角色权限配置发生变更时调用
   */
  clearRoleCache(role: string): void {
    // let clearedCount = 0

    for (const [key] of this.cache) {
      if (key.includes(role)) {
        this.cache.delete(key)
        // clearedCount++
      }
    }

    this.stats.cacheSize = this.cache.size

    // if (import.meta.env.DEV) {
    //   console.log(`🧹 [权限缓存] 清理角色缓存: ${role}, 清理${clearedCount}条记录`)
    // }
  }

  /**
   * 清理所有缓存
   */
  clearAll(): void {
    // const size = this.cache.size
    this.cache.clear()
    this.stats.cacheSize = 0

    // if (import.meta.env.DEV) {
    //   console.log(`🧹 [权限缓存] 清理所有缓存, 清理${size}条记录`)
    // }
  }

  /**
   * 手动清理过期缓存
   */
  cleanup(): void {
    const now = Date.now()
    // let clearedCount = 0

    for (const [key, entry] of this.cache) {
      if (now > entry.expires) {
        this.cache.delete(key)
        // clearedCount++
      }
    }

    this.stats.cacheSize = this.cache.size
    this.stats.lastCleanup = now

    // if (import.meta.env.DEV && clearedCount > 0) {
    //   console.log(`🧹 [权限缓存] 定期清理: 清理${clearedCount}条过期记录`)
    // }
  }

  /**
   * 获取缓存性能统计
   */
  getStats(): CacheStats {
    return {
      ...this.stats,
      hitRate: this.stats.totalChecks > 0
        ? (this.stats.hits / this.stats.totalChecks) * 100
        : 0,
      cacheSize: this.cache.size,
    }
  }

  /**
   * 获取详细的缓存信息
   */
  getDetailedInfo(): {
    stats: CacheStats
    config: CacheConfig
    topHitEntries: Array<{ key: string, hits: number, age: number }>
    recentChecks: number[]
  } {
    // 获取命中次数最多的条目
    const topHitEntries = Array.from(this.cache.entries())
      .map(([key, entry]) => ({
        key,
        hits: entry.hits,
        age: Date.now() - entry.created,
      }))
      .sort((a, b) => b.hits - a.hits)
      .slice(0, 10)

    return {
      stats: this.getStats(),
      config: this.config,
      topHitEntries,
      recentChecks: this.checkTimes.slice(-50), // 最近50次检查时间
    }
  }

  /**
   * 预热缓存
   * 预先缓存一些常用的权限检查结果
   */
  async warmup(commonChecks: Array<{ key: string, value: boolean }>): Promise<void> {
    for (const { key, value } of commonChecks) {
      this.set(key, value)
    }

    // if (import.meta.env.DEV) {
    //   console.log(`🔥 [权限缓存] 缓存预热完成: ${commonChecks.length}条记录`)
    // }
  }

  /**
   * 启用/禁用缓存
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled

    if (!enabled) {
      this.clearAll()
      this.stopAutoCleanup()
    }
    else {
      this.startAutoCleanup()
    }

    // if (import.meta.env.DEV) {
    //   console.log(`🔧 [权限缓存] 缓存${enabled ? '启用' : '禁用'}`)
    // }
  }

  /**
   * 销毁缓存服务
   */
  destroy(): void {
    this.stopAutoCleanup()
    this.clearAll()
    this.checkTimes = []

    // if (import.meta.env.DEV) {
    //   console.log('💥 [权限缓存] 缓存服务已销毁')
    // }
  }

  /**
   * 启动自动清理
   */
  private startAutoCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer)
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup()
    }, this.config.cleanupInterval)
  }

  /**
   * 停止自动清理
   */
  private stopAutoCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer)
      this.cleanupTimer = undefined
    }
  }

  /**
   * 记录命中
   */
  private recordHit(checkTime: number): void {
    this.stats.hits++
    this.recordCheckTime(checkTime)
  }

  /**
   * 记录未命中
   */
  private recordMiss(checkTime: number): void {
    this.stats.misses++
    this.recordCheckTime(checkTime)
  }

  /**
   * 记录检查时间
   */
  private recordCheckTime(checkTime: number): void {
    this.checkTimes.push(checkTime)

    // 只保留最近1000次检查时间
    if (this.checkTimes.length > 1000) {
      this.checkTimes = this.checkTimes.slice(-1000)
    }

    // 更新平均检查时间
    this.stats.averageCheckTime = this.checkTimes.reduce((sum, time) => sum + time, 0) / this.checkTimes.length
  }
}

/**
 * 创建默认权限缓存实例
 */
export function createPermissionCache(config?: Partial<CacheConfig>): PermissionCache {
  return new PermissionCache(config)
}

/**
 * 全局权限缓存实例（单例）
 */
export const permissionCache = createPermissionCache()

/**
 * 开发环境全局调试工具
 */
if (import.meta.env.DEV) {
  // 将缓存实例暴露到全局，方便调试
  (window as any).__PERMISSION_CACHE__ = {
    cache: permissionCache,
    stats: () => permissionCache.getStats(),
    info: () => permissionCache.getDetailedInfo(),
    clear: () => permissionCache.clearAll(),
    warmup: (checks: Array<{ key: string, value: boolean }>) => permissionCache.warmup(checks),
  }

  // console.log('🛠️ [权限缓存] 调试工具已注册到 window.__PERMISSION_CACHE__')
}
